// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Utils
import BezierEasing from 'bezier-easing';
import { convertToCssTransformation } from '../../../utils/cssUtil';
import {
    getFocusedElementTransformation,
    getOriginalElementTransformation,
    getPresentationModeFocusLayerBoundingRect,
} from './focusModeUtils';
import requestPromisedAnimationFrame from '../../../../common/utils/lib/requestPromisedAnimationFrame';

// Selectors
import { getElements } from '../../../element/selectors/elementsSelector';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

// Actions
import { focusElementActive, focusedElementClicked } from '../presentationActions';

// Components
import Element from '../../../element/Element';
import FocusModeHighQualityImageRenderer from './FocusModeHighQualityImageRenderer';
import Measure from '../../../../node_module_clones/react-measure/src/Measure';

// Types
import { BrowserEngineType } from '../../../../common/platform/platformTypes';

// Styles
import './FocusModeElement.scss';

const FOCUS_MODE_ELEMENT_PADDING_BOTTOM = 50;

/**
 * The bezier easing function to use for the zoom to fit animation.
 */
const zoomToFitBezierFunc = BezierEasing(0.645, 0.045, 0.355, 1);

const updateVal = (t, startVal, endVal) => {
    if (t >= 1) return endVal;

    const factor = zoomToFitBezierFunc(t);

    return startVal + (endVal - startVal) * factor;
};

const animateTransform = async (focusElementNode, startingTransformation, finalTransformation) => {
    const startTime = Date.now();
    const duration = 400;

    let t = 0;

    while (t <= 1) {
        t = (Date.now() - startTime) / duration;

        let newX = updateVal(t, startingTransformation.position.x, finalTransformation.position.x);
        let newY = updateVal(t, startingTransformation.position.y, finalTransformation.position.y);
        let newScale = updateVal(t, startingTransformation.scale, finalTransformation.scale);

        await requestPromisedAnimationFrame(() => {
            if (!focusElementNode) return;

            const cssTransform = convertToCssTransformation({ x: newX, y: newY }, newScale);
            focusElementNode.style.transform = cssTransform?.transform;
            focusElementNode.style.WebkitTransform = cssTransform?.WebkitTransform;
        });
    }
};

const makeMapStateToProps = (initialState, initialProps) => (state) => ({
    element: getElements(state).get(initialProps.elementId),
    platformDetails: getPlatformDetailsSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
    dispatchFocusElementActive: () => dispatch(focusElementActive()),
    dispatchFocusedElementClicked: (elementId) => dispatch(focusedElementClicked(elementId)),
});

const FocusModeElement = (props) => {
    const { elementId, element, initialZoomScale, animateLeaveRef, dispatchFocusElementActive, platformDetails } =
        props;

    // Initially use the standard quality image to ensure that there's no flash
    // when focusing the element for the first time.
    const [renderHighQualityImages, setRenderHighQualityImages] = React.useState(false);

    const isAnimatingRef = React.useRef(false);
    const focusModeElementRef = React.useRef();
    const focusModeElementShadowRef = React.useRef();
    const wrapperRef = React.useRef();
    const currentTransformationRef = React.useRef();
    const renderHighQualityImageCallbackRef = React.useRef();

    React.useEffect(() => {
        if (!focusModeElementRef.current) return;

        const startingTransformation = getOriginalElementTransformation(elementId, initialZoomScale);
        const finalTransformation = getFocusedElementTransformation(elementId, initialZoomScale, element);
        const focusLayerRect = getPresentationModeFocusLayerBoundingRect();

        if (!startingTransformation || !finalTransformation || !focusLayerRect) return;

        isAnimatingRef.current = true;

        currentTransformationRef.current = finalTransformation;

        const focusLayerOverlayDomEl = document.querySelector('.presentation-mode-focus-overlay');
        focusLayerOverlayDomEl.style.opacity = 1;

        // Then shift it to the new position
        // NOTE: We need to set the width of the FocusModeElement, which is the parent of the rendered Element,
        //  so that if the element is in a column it renders at the same width when it's focused
        const focusModeElementWidth = startingTransformation.size.width / initialZoomScale;
        focusModeElementRef.current.style.width = `${Math.round(focusModeElementWidth)}px`;

        const paddingBottom = FOCUS_MODE_ELEMENT_PADDING_BOTTOM / finalTransformation.scale;

        const cssTransform = convertToCssTransformation(startingTransformation.position, startingTransformation.scale);
        focusModeElementRef.current.style.transform = cssTransform?.transform;
        focusModeElementRef.current.style.WebkitTransform = cssTransform?.WebkitTransform;

        // Only add the padding bottom if the size of the transformed element is larger than the size of the
        //  focus layer. Otherwise, we might end up with only a small amount of overflow in the padding which
        //  causes unnecessary scroll bars to show
        const requiresPaddingBottom =
            focusLayerRect.height < finalTransformation.position.y + finalTransformation.size.height;

        if (requiresPaddingBottom) {
            focusModeElementRef.current.style.paddingBottom = `${paddingBottom}px`;

            // The shadow should start above the padding at the bottom
            focusModeElementShadowRef.current.style.bottom = `${paddingBottom}px`;
        }

        focusModeElementShadowRef.current.style.opacity = 1;

        // We need to set the width and height of a wrapper div, otherwise Chrome struggles
        // to render the correct scrollable area
        wrapperRef.current.style.minWidth = `${finalTransformation.size.width}px`;
        const minHeight = finalTransformation.size.height + finalTransformation.position.y;
        wrapperRef.current.style.minHeight = `${minHeight}px`;

        animateTransform(focusModeElementRef.current, startingTransformation, finalTransformation).then(() => {
            // Then change to a high quality image to ensure the zoomed in version looks crisp
            platformDetails.browserEngine !== BrowserEngineType.safari && setRenderHighQualityImages(true);

            renderHighQualityImageCallbackRef.current?.(true);

            isAnimatingRef.current = false;
            dispatchFocusElementActive();
        });
    }, []);

    /**
     * Animate the return of the element to its original position.
     */
    animateLeaveRef.current = React.useCallback(async () => {
        if (!focusModeElementRef.current) return;

        // Hide the high quality image
        renderHighQualityImageCallbackRef.current?.(false);

        isAnimatingRef.current = true;

        const focusLayerOverlayDomEl = document.querySelector('.presentation-mode-focus-overlay');
        focusLayerOverlayDomEl.style.opacity = null;

        focusModeElementShadowRef.current.style.opacity = null;

        const startingTransformation = getFocusedElementTransformation(elementId, initialZoomScale, element);
        const finalTransformation = getOriginalElementTransformation(elementId, initialZoomScale);

        await animateTransform(focusModeElementRef.current, startingTransformation, finalTransformation);

        isAnimatingRef.current = false;
    }, []);

    /**
     * When the element changes size recalculate its transformation so it's centred again.
     */
    const onMeasure = React.useCallback(async () => {
        // Don't perform measurements while animating in or out
        if (isAnimatingRef.current) return;

        if (!currentTransformationRef.current || !focusModeElementRef.current) return;

        renderHighQualityImageCallbackRef.current?.(false);

        const targetTransformation = getFocusedElementTransformation(elementId, initialZoomScale, element);

        animateTransform(focusModeElementRef.current, currentTransformationRef.current, targetTransformation).then(
            () => {
                renderHighQualityImageCallbackRef.current?.(true);
            },
        );

        currentTransformationRef.current = targetTransformation;
    }, []);

    /**
     * Reposition the FocusModeElement when the window resizes.
     */
    React.useEffect(() => {
        window.addEventListener('resize', onMeasure);

        return () => {
            window.removeEventListener('resize', onMeasure);
        };
    }, []);

    /**
     * Focus the focus-mode-element-wrapper on mount to ensure that the canvas won't scroll when the arrow keys
     * are pressed.
     */
    React.useEffect(() => {
        if (!wrapperRef.current) return;
        wrapperRef.current.focus();
    }, []);

    return (
        <div className="focus-mode-element-wrapper" tabIndex={-1} ref={wrapperRef}>
            <div className="FocusModeElement" ref={focusModeElementRef}>
                <div className="focus-mode-element-shadow" ref={focusModeElementShadowRef} />
                <Measure whitelist={['height']} onMeasure={onMeasure}>
                    <Element {...props} isFocusedForegroundElement renderHighQualityImages={renderHighQualityImages} />
                </Measure>
            </div>
            {platformDetails.browserEngine === BrowserEngineType.safari && (
                <FocusModeHighQualityImageRenderer
                    renderHighQualityImageCallbackRef={renderHighQualityImageCallbackRef}
                    focusModeElementRef={focusModeElementRef}
                    element={element}
                />
            )}
        </div>
    );
};

FocusModeElement.propTypes = {
    currentBoardId: PropTypes.string,
    dispatchFocusElementActive: PropTypes.func,
    dispatchFocusedElementClicked: PropTypes.func,
    element: PropTypes.object,
    elementId: PropTypes.string,
    gridSize: PropTypes.number,
    permissions: PropTypes.number,
    initialZoomScale: PropTypes.number,

    animateLeaveRef: PropTypes.object,
    platformDetails: PropTypes.object,
};

export default connect(makeMapStateToProps, mapDispatchToProps)(FocusModeElement);
